/**
 * VariableEditorContext
 *
 * React context that provides FlowDocument.variables to the screen editor.
 * Loads variables directly from Firebase (not via props chain) so any section
 * can access and modify variables without prop drilling.
 *
 * Usage:
 *   <VariableEditorProvider projectId={...} onboardingId={...} versionId={...}>
 *     <DefaultOnboardingContentEditorV2 ... />
 *   </VariableEditorProvider>
 *
 * Sections consume via:
 *   const { variables, createVariable, ... } = useVariableEditor();
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { FunnelVariable } from '@segmently/schema-models/v2';
import { flowVariablesService } from '@modules/firebase-core/services/FlowVariablesService';
import type { FlowVariablesHost } from '@modules/reactFlow/services/shared/FlowCoordinator';
import {
  variableFactory,
  variableCompatibilityService,
  type CreateVariableParams,
} from '@modules/v2-variable-utils';

export interface VariableEditorContextValue {
  /** All flow-level variables keyed by id */
  variables: Record<string, FunnelVariable>;
  /** True while initial load is in progress */
  loading: boolean;
  /** Monotonic counter — increments on every variables mutation (for memo deps) */
  variablesVersion: number;
  /** Map of variableId → array of stepIds that bind it via FunnelVariable.boundScreenIds */
  variableUsageMap: Record<string, string[]>;
  /** Create a new variable and save to Firebase */
  createVariable(params: CreateVariableParams): Promise<FunnelVariable>;
  /** Update an existing variable in Firebase */
  updateVariable(variable: FunnelVariable): Promise<void>;
  /** Delete a variable by ID from Firebase */
  deleteVariable(variableId: string): Promise<void>;
  /** Get variables compatible with a given StepKind */
  getCompatibleVariables(stepKind: string): FunnelVariable[];
  /** Reload variables from Firebase */
  refresh(): Promise<void>;
}

const defaultValue: VariableEditorContextValue = {
  variables: {},
  loading: true,
  variablesVersion: 0,
  variableUsageMap: {},
  createVariable: async () => { throw new Error('VariableEditorProvider not mounted'); },
  updateVariable: async () => { throw new Error('VariableEditorProvider not mounted'); },
  deleteVariable: async () => { throw new Error('VariableEditorProvider not mounted'); },
  getCompatibleVariables: () => [],
  refresh: async () => {},
};

const VariableEditorContext = createContext<VariableEditorContextValue>(defaultValue);

export interface VariableEditorProviderProps {
  projectId?: string;
  onboardingId?: string;
  versionId?: string;
  /** Pre-computed map of variableId → stepIds that bind it (from canvas level) */
  screensBindingMap?: Record<string, string[]>;
  /**
   * Optional storage adapter for hosts whose variables live outside
   * flowVariablesService (e.g. article drafts persist into
   * flowDocument.variables via their coordinator). When provided, ALL
   * load/save/delete operations route through it and the
   * projectId/onboardingId/versionId triple is not required.
   */
  variablesAdapter?: FlowVariablesHost;
  children: React.ReactNode;
}

export const VariableEditorProvider: React.FC<VariableEditorProviderProps> = ({
  projectId,
  onboardingId,
  versionId,
  screensBindingMap,
  variablesAdapter,
  children,
}) => {
  const [variables, setVariables] = useState<Record<string, FunnelVariable>>({});
  const [loading, setLoading] = useState(true);
  const [variablesVersion, setVariablesVersion] = useState(0);
  const bumpVersion = useCallback(() => setVariablesVersion(v => v + 1), []);

  // Sync external screensBindingMap to internal state
  const variableUsageMap = useMemo(() => screensBindingMap ?? {}, [screensBindingMap]);

  // Load variables from Firebase on mount and when IDs change
  const loadVariables = useCallback(async () => {
    if (!variablesAdapter && (!projectId || !onboardingId || !versionId)) {
      setVariables({});
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const vars = variablesAdapter
        ? await variablesAdapter.getFlowVariables()
        : await flowVariablesService.getVariables(projectId!, onboardingId!, versionId!);
      setVariables(vars);
      bumpVersion();
    } catch (error) {
      console.error('[VariableEditorContext] Failed to load variables:', error);
      setVariables({});
    } finally {
      setLoading(false);
    }
  }, [projectId, onboardingId, versionId, variablesAdapter, bumpVersion]);

  useEffect(() => {
    loadVariables();
  }, [loadVariables]);

  const persistVariable = useCallback(async (variable: FunnelVariable): Promise<void> => {
    if (variablesAdapter) {
      await variablesAdapter.saveFlowVariable(variable);
      return;
    }
    if (!projectId || !onboardingId || !versionId) {
      throw new Error('Missing projectId, onboardingId, or versionId');
    }
    await flowVariablesService.saveVariable(projectId, onboardingId, versionId, variable);
  }, [projectId, onboardingId, versionId, variablesAdapter]);

  const createVariable = useCallback(async (params: CreateVariableParams): Promise<FunnelVariable> => {
    if (!variablesAdapter && (!projectId || !onboardingId || !versionId)) {
      throw new Error('Missing projectId, onboardingId, or versionId');
    }

    // Generate unique ID avoiding collisions with existing variables
    const existingIds = Object.keys(variables);
    const id = params.id || variableFactory.generateId(params.name, existingIds);
    const variable = variableFactory.create({ ...params, id });

    // Optimistic update FIRST — UI reflects change immediately
    setVariables(prev => ({ ...prev, [variable.id]: variable }));
    bumpVersion();

    try {
      await persistVariable(variable);
    } catch (error) {
      // Revert on failure
      console.error('[VariableEditorContext] createVariable failed, reverting:', error);
      loadVariables();
      throw error;
    }

    return variable;
  }, [projectId, onboardingId, versionId, variablesAdapter, variables, persistVariable, loadVariables, bumpVersion]);

  const updateVariable = useCallback(async (variable: FunnelVariable): Promise<void> => {
    if (!variablesAdapter && (!projectId || !onboardingId || !versionId)) {
      throw new Error('Missing projectId, onboardingId, or versionId');
    }

    // Optimistic update FIRST — UI reflects change immediately
    setVariables(prev => ({ ...prev, [variable.id]: variable }));
    bumpVersion();

    try {
      await persistVariable(variable);
    } catch (error) {
      // Revert on failure — reload from Firebase
      console.error('[VariableEditorContext] updateVariable failed, reverting:', error);
      loadVariables();
      throw error;
    }
  }, [projectId, onboardingId, versionId, variablesAdapter, persistVariable, loadVariables, bumpVersion]);

  const deleteVariable = useCallback(async (variableId: string): Promise<void> => {
    if (variablesAdapter) {
      await variablesAdapter.deleteFlowVariable(variableId);
    } else {
      if (!projectId || !onboardingId || !versionId) {
        throw new Error('Missing projectId, onboardingId, or versionId');
      }
      await flowVariablesService.deleteVariable(projectId, onboardingId, versionId, variableId);
    }

    // Optimistic update
    setVariables(prev => {
      const next = { ...prev };
      delete next[variableId];
      return next;
    });
    bumpVersion();
  }, [projectId, onboardingId, versionId, variablesAdapter, bumpVersion]);

  const getCompatibleVariables = useCallback((stepKind: string): FunnelVariable[] => {
    const compatibleTypes = variableCompatibilityService.getCompatibleTypes(stepKind);
    if (compatibleTypes.length === 0) return [];

    return Object.values(variables).filter(v => compatibleTypes.includes(v.type));
  }, [variables]);

  const value = useMemo<VariableEditorContextValue>(() => ({
    variables,
    loading,
    variablesVersion,
    variableUsageMap,
    createVariable,
    updateVariable,
    deleteVariable,
    getCompatibleVariables,
    refresh: loadVariables,
  }), [variables, loading, variablesVersion, variableUsageMap, createVariable, updateVariable, deleteVariable, getCompatibleVariables, loadVariables]);

  return (
    <VariableEditorContext.Provider value={value}>
      {children}
    </VariableEditorContext.Provider>
  );
};

/** Hook to access variable editor context */
export const useVariableEditor = () => useContext(VariableEditorContext);
